import type { Route } from 'vue-router'
import { $getCookie, $getQueryParam, $normalizeQueryString } from '@/helpers'
import { useEffectiveSubmitTrackStore } from '@/stores'
import { sleep } from '@/utils'
import { clearEmpty } from '@/utils/clearEmpty'

interface CollectAttributionOptions {
  waitForCookies?: boolean
  syncStore?: boolean
}

/**
 * 通用Cookie轮询函数
 * @param {string[]} cookieNames - 需要获取的Cookie名称数组
 * @param {number} [maxRetries=20] - 最大重试次数（默认20）
 * @param {number} [retryInterval=100] - 重试间隔毫秒数（默认100）
 * @returns {Promise<Object>} - 包含Cookie键值对的对象（未获取到的值为undefined）
 */
async function pollCookies(cookieNames: string[], maxRetries = 20, retryInterval = 100) {
  const results: any = {}
  for (let i = 0; i < maxRetries; i++) {
    cookieNames.forEach((name) => {
      if (!results[name])
        results[name] = $getCookie(name) || undefined
    })
    if (cookieNames.every(name => results[name] !== undefined))
      break
    if (i < maxRetries - 1)
      await sleep(retryInterval)
  }

  return results
}

/**
 * 解码 URL 参数
 * @param {string} value - 需要解码的参数值
 * @returns {string} 解码后的参数值
 */
function decodeParam(value: string): string {
  if (!value)
    return value
  try {
    return decodeURIComponent(value)
  }
  catch {
    // 如果解码失败，返回原值
    return value
  }
}

function getRouteQueryValue(route: Route | undefined, key: string) {
  const value = route?.query?.[key]
  if (Array.isArray(value))
    return value[0] ?? ''
  if (value == null)
    return ''
  return String(value)
}

function getQueryParamValue(key: string, route?: Route) {
  const routeValue = getRouteQueryValue(route, key)
  if (routeValue)
    return decodeParam(routeValue)
  return decodeParam($getQueryParam(key) || '')
}

function getRouteSearchParams(route?: Route) {
  if (!route)
    return new URLSearchParams($normalizeQueryString(window.location.search))

  const searchParams = new URLSearchParams()
  Object.entries(route.query || {}).forEach(([key, value]) => {
    if (Array.isArray(value)) {
      value.forEach((item) => {
        if (item != null && item !== '')
          searchParams.append(key, String(item))
      })
      return
    }

    if (value != null && value !== '')
      searchParams.set(key, String(value))
  })
  return searchParams
}

/**
 * 获取通用的查询参数（所有参数会自动 decode）
 * @returns {Object} 包含所有通用查询参数的对象
 */
export function getCommonQueryParams(route?: Route) {
  return clearEmpty({
    pId: getQueryParamValue('pId', route),
    contentId: getQueryParamValue('contentId', route),
    initIndex: getQueryParamValue('initIndex', route),
    utm_campaign_id: getQueryParamValue('utm_campaign_id', route),
    utm_campaign: getQueryParamValue('utm_campaign', route),
    utm_adset: getQueryParamValue('utm_adset', route),
    utm_adset_id: getQueryParamValue('utm_adset_id', route),
    utm_ad_name: getQueryParamValue('utm_ad_name', route),
    utm_ad_id: getQueryParamValue('utm_ad_id', route),
    utm_source: getQueryParamValue('utm_source', route),
    utm_medium: getQueryParamValue('utm_medium', route),
    utm_site_source_name: getQueryParamValue('utm_site_source_name', route),
    utm_id: getQueryParamValue('utm_id', route),
    utm_content: getQueryParamValue('utm_content', route),
    utm_term: getQueryParamValue('utm_term', route),
    bizAdId: getQueryParamValue('bizAdId', route),
  })
}

function collectTTParams(ttclid: string, _ttp: string, route?: Route) {
  return clearEmpty({
    _fbc: ttclid,
    _fbp: _ttp,
    attributeType: 'web',
    url: decodeParam(window.location.href),
    ua: navigator.userAgent,
    ...getCommonQueryParams(route),
  })
}

function collectFBParams(_fbp: string, _fbc: string, route?: Route) {
  return clearEmpty({
    _fbp,
    _fbc,
    attributeType: 'web',
    url: decodeParam(window.location.href),
    ua: navigator.userAgent,
    ...getCommonQueryParams(route),
  })
}

function collectBizAdParams(route?: Route) {
  return getCommonQueryParams(route)
}

function collectTalentParams(route?: Route) {
  // 标记为达人分享进入, 持久化以便后续页面(如 banner 展示)判断
  localStorage.setItem('IS_TALENT_SHARE', '1')
  const searchParams = getRouteSearchParams(route)
  const params: Record<string, string> = {}
  searchParams.forEach((value, key) => {
    if (value)
      params[key] = decodeParam(value)
  })
  return params
}

function collectAxonParams(route?: Route) {
  const searchParams = getRouteSearchParams(route)
  const params: Record<string, string> = {}
  searchParams.forEach((value, key) => {
    if (value)
      params[key] = decodeParam(value)
  })

  const isBookOrChapterPage = ['book', 'chapter', 'chapter-scroll'].includes(route?.name as string)
  if (isBookOrChapterPage && route?.params?.contentId)
    params.contentId = String(route.params.contentId)

  params.attributeType = 'web'
  return params
}

// Mintegral {creative_id}/{campaign_id} 等宏为官方可选参数(动态组合/Playable 素材无单创意维度),
// 未返回时替换为 0 或留空,也可能保留未解析的字面量占位符(如 "{creative_id}");
// 0 不是真实 ID,与空值/非数字一并视为无效
function isValidMintegralId(value?: string) {
  return !!value && /^\d+$/.test(value) && Number(value) > 0
}

function collectMintegralParams(route?: Route) {
  const params = collectAxonParams(route)

  // 层级 ID 缺省回退,与 Google 分支口径一致:下级无效用上级覆盖(campaign → adset → ad 逐级向下补);
  // 回退后的 utm_ad_id 为 Offer/Adset 维度 ID,非真实 Creative ID,仅保证三级 ID 不缺失
  const campaignId = isValidMintegralId(params.utm_campaign_id) ? params.utm_campaign_id : ''
  const adsetId = isValidMintegralId(params.utm_adset_id) ? params.utm_adset_id : campaignId
  const adId = isValidMintegralId(params.utm_ad_id) ? params.utm_ad_id : adsetId

  return clearEmpty({
    ...params,
    utm_campaign_id: campaignId,
    utm_adset_id: adsetId,
    utm_ad_id: adId,
  })
}

const GOOGLE_CLICK_ID_KEYS = ['gclid', 'gbraid', 'wbraid'] as const

// Google 点击参数清洗:过滤控制字符并限长,防脏数据进 _fbc
function sanitizeGoogleParam(value: string) {
  // eslint-disable-next-line no-control-regex
  return value.replace(/[\x00-\x1F\x7F]/g, '').slice(0, 512)
}

function hasGoogleClickId(route?: Route) {
  return GOOGLE_CLICK_ID_KEYS.some(key => !!getQueryParamValue(key, route))
}

// 纯数字才视为有效 ID(过滤空值与 Google 未替换的字面量占位符,如 "{adgroupid}")
function isNumericId(value?: string) {
  return !!value && /^\d+$/.test(value)
}

function collectGoogleParams(route?: Route) {
  const gclid = sanitizeGoogleParam(getQueryParamValue('gclid', route))
  const gbraid = sanitizeGoogleParam(getQueryParamValue('gbraid', route))
  const wbraid = sanitizeGoogleParam(getQueryParamValue('wbraid', route))

  // _fbc 传单字段 JSON(如 {"gclid":"xxx"}),键名供后端区分点击 ID 类型;
  // 理论上三者只会存在一个,按 gclid > gbraid > wbraid 优先级取一;_fbp 不传,与 FB 严格隔离;
  // 三个点击 ID 全空则不提交点击归因(BR-01),仅回传通用 utm 参数
  const clickJson = gclid
    ? JSON.stringify({ gclid })
    : gbraid
      ? JSON.stringify({ gbraid })
      : wbraid ? JSON.stringify({ wbraid }) : ''

  const commonParams = getCommonQueryParams(route)

  // 普通广告与 PMax 的 adset 占位符不同:utm_adset_id={adgroupid}、utm_assetgroup_adset_id={assetgroupid};
  // Google 只替换适用于当前广告类型的占位符,另一个为空或保留字面量(如 "{assetgroupid}"),
  // 故取二者中为纯数字的那个归一为 utm_adset_id 上报
  const assetGroupAdsetId = getQueryParamValue('utm_assetgroup_adset_id', route)
  const numericAdsetId = [commonParams.utm_adset_id, assetGroupAdsetId].find(isNumericId)

  // 层级 ID 缺省回退:下级缺失用上级覆盖(campaign → adset → ad 逐级向下补);
  // utm_ad_id 同样过纯数字校验:PMax 不支持 {creative} 占位符,防未替换字面量脏传
  const utmCampaignId = commonParams.utm_campaign_id
  const utmAdsetId = numericAdsetId || utmCampaignId
  const utmAdId = isNumericId(commonParams.utm_ad_id) ? commonParams.utm_ad_id : utmAdsetId

  return clearEmpty({
    _fbc: clickJson,
    attributeType: 'web',
    url: decodeParam(window.location.href),
    ua: navigator.userAgent,
    ...commonParams,
    utm_campaign_id: utmCampaignId,
    utm_adset_id: utmAdsetId,
    utm_ad_id: utmAdId,
  })
}

// 广告归因渠道白名单(utm_source),新增渠道需同步维护
const AD_UTM_SOURCES = ['axon', 'talent', 'google', 'taboola', 'snapchat', 'mintegral']

export function hasAdAttributionParams(route?: Route) {
  return (
    AD_UTM_SOURCES.includes(getQueryParamValue('utm_source', route))
    || hasGoogleClickId(route)
    || !!getQueryParamValue('bizAdId', route)
    || !!getQueryParamValue('ttclid', route)
    || !!getQueryParamValue('fbclid', route)
  )
}

export async function collectAttributionParams(route?: Route, options: CollectAttributionOptions = {}) {
  const { waitForCookies = true, syncStore = false } = options

  let params: Record<string, any> = {}

  if (getQueryParamValue('utm_source', route) === 'axon') {
    params = collectAxonParams(route)
    if (syncStore)
      useEffectiveSubmitTrackStore().$setParams(params)
    return params
  }

  if (getQueryParamValue('utm_source', route) === 'talent') {
    params = collectTalentParams(route)
    if (syncStore)
      useEffectiveSubmitTrackStore().$setParams(params)
    return params
  }

  // taboola / snapchat 渠道与 axon 采集口径一致
  if (['taboola', 'snapchat'].includes(getQueryParamValue('utm_source', route))) {
    params = collectAxonParams(route)
    if (syncStore)
      useEffectiveSubmitTrackStore().$setParams(params)
    return params
  }

  // mintegral 渠道在 axon 口径基础上做层级 ID 清洗回退
  // (归因参数 utm_mtg_campaign_uuid/utm_mtg_click_id/utm_mtgtk 全在 URL 上,无需轮询 cookie)
  if (getQueryParamValue('utm_source', route) === 'mintegral') {
    params = collectMintegralParams(route)
    if (syncStore)
      useEffectiveSubmitTrackStore().$setParams(params)
    return params
  }

  // google 渠道:gclid 直接在 URL 上,无需轮询 cookie(与 FB 分支不同)
  if (getQueryParamValue('utm_source', route) === 'google' || hasGoogleClickId(route)) {
    params = collectGoogleParams(route)
    if (syncStore)
      useEffectiveSubmitTrackStore().$setParams(params)
    return params
  }

  if (getQueryParamValue('bizAdId', route))
    params = collectBizAdParams(route)

  // 轮询上限按 fbevents.js 加载耗时 P90 定为 1s(10 次 × 100ms,数据见 F3 文档改动三),被拦截用户不再白等 2s
  if (getQueryParamValue('ttclid', route)) {
    const cookies = waitForCookies
      ? await pollCookies(['_ttp', 'ttclid'], 10)
      : { _ttp: $getCookie('_ttp') || '', ttclid: $getCookie('ttclid') || '' }
    const ttclid = cookies.ttclid || getQueryParamValue('ttclid', route)
    params = collectTTParams(ttclid, cookies._ttp || '', route)
  }

  if (getQueryParamValue('fbclid', route)) {
    const cookies = waitForCookies
      ? await pollCookies(['_fbp', '_fbc'], 10)
      : { _fbp: $getCookie('_fbp') || '', _fbc: $getCookie('_fbc') || '' }
    // 1s 内取到 cookie 就用;取不到按 Meta 规范由 fbclid 现场合成,保住强匹配信号(_fbp 无法合成,缺失即缺失)
    if (!cookies._fbc)
      cookies._fbc = `fb.1.${Date.now()}.${getQueryParamValue('fbclid', route)}`
    params = collectFBParams(cookies._fbp || '', cookies._fbc, route)
  }

  if (syncStore)
    useEffectiveSubmitTrackStore().$setParams(params)

  return params
}

export default function useFacebookTrack() {
  return {
    async init(route?: Route) {
      return collectAttributionParams(route, {
        waitForCookies: true,
        syncStore: true,
      })
    },
  }
}

// tt 链接
// https://h5-fb-test.rocnovel.com/content/228211894582247424/?
// utm_campaign=0313-test
// &utm_adset=0313test
// &utm_campaign_id=1826471701030929
// &utm_adset_id=1826471701030961
// &utm_ad_id=1826471704216625
// &utm_source=tt
// &utm_site_source_name=tt
// &utm_medium=tt
// &utm_ad_name=1_HGDQM4Zu.mp4_2025-03-13%2017%3A30%3A21
// &contentId=228211894582247424
// &initIndex=null
// &pId=231115327815557120
// &ttclid=E_C_P_CsEBuiB9Z7Tl5oEujM4EW7KLVr8lMm_0BQWd-5U9sv-1OjDqq8Ls-e2cYogb5dVaCCkk0_1mWvqMPuj0-3iqquh8EURMnMXbW7UjXTd6JtytI1K8xY14yn5K3zEk5Zd0lV_xRbT6h--bYRoeiZMFaVFr-eeHIulJ-xb7MOxDI5N1EOwGtE56BOo5fYcfH2n-EdgU3LiiMXMrkB8mOtMU5sDC0s6xsIVD_xE7VxMZ_bIBxmjz69ioRyubz2cJYGVlMadLDhIEdjIuMA

// fb 链接
// https://h5-fb-test.rocnovel.com/content/190052388312711168/?
// utm_campaign=Panda_rocnovel_AND_182899821023596544_HYJ03_1125_1014V_test
// &utm_adset=V
// &utm_campaign_id=120213965296190162
// &utm_medium=Facebook_Desktop_Feed
// &utm_adset_id=120213965296440162
// &utm_ad_id=120213965296800162
// &utm_source=fb
// &utm_ad_name=1125-test
// &utm_site_source_name=fb
// &contentId=190052388312711168
// &initIndex=null
// &pId=191922336765050880
// &utm_id=120213965296190162
// &utm_content=120213965296800162
// &utm_term=120213965296440162
// &fbclid=IwY2xjawGxECFleHRuA2FlbQIxMAABHftuWjJ_vybW7NIPAybd0LCDJHwywHTlt_PqpM0YK9gjKRYPBZaGhFgvOA_aem_JN3DZ4ShLxryc8Jdau1ECA

// https://h5-fb-test.rocnovel.com/content/N0vJJlpLhmNfmtwJ/171582742764982272?utm_campaign=3232333&utm_content=32323233&utm_medium=paid&utm_source=axon&utm_term=utm_ter32332&pId=375588088024928256&pixel_code=applovin33
